import { v4 as uuid } from 'uuid';
import { LinkedFile } from '..';
import { ResourceCredential, ResourceDraft, ResourceFile } from '../api';
import { ExtendedTruvityClient } from '../ExtendedClient';
import { LinkedCredential, Rfc3339Date, Rfc3339DateTime } from './models';

const dateString = '2023-06-30';
const timeString = '13:00:25.525';
const zeroTimeString = '00:00:00.000';
const referenceDateTimeString = `${dateString}T${timeString}Z`;

describe('models', () => {
    it('should serialize/deserialize correctly Rfc3339DateTime instance', async () => {
        const rfc3339DateTime = new Rfc3339DateTime(referenceDateTimeString);
        const serializedValue = JSON.stringify(rfc3339DateTime);

        expect(serializedValue).toEqual(`"${referenceDateTimeString}"`);
        expect(new Rfc3339DateTime(JSON.parse(serializedValue))).toEqual(rfc3339DateTime);
    });

    it('should convert correctly to different types the Rfc3339DateTime instance', async () => {
        const rfc3339DateTime = new Rfc3339DateTime(referenceDateTimeString);

        expect(rfc3339DateTime.toDate()).toEqual(new Date(referenceDateTimeString));
        expect(rfc3339DateTime.toString()).toEqual(referenceDateTimeString);
    });

    it('should serialize/deserialize correctly Rfc3339Date instance', async () => {
        const rfc3339Date = new Rfc3339Date(referenceDateTimeString);
        const serializedValue = JSON.stringify(rfc3339Date);

        expect(serializedValue).toEqual(`"${dateString}"`);
        expect(new Rfc3339Date(JSON.parse(serializedValue))).toEqual(rfc3339Date);
    });

    it('should convert correctly to different types the Rfc3339Date instance', async () => {
        const rfc3339Date = new Rfc3339Date(referenceDateTimeString);

        expect(rfc3339Date.toDate()).toEqual(new Date(`${dateString}T${zeroTimeString}Z`));
        expect(rfc3339Date.toString()).toEqual(dateString);
    });

    const tenantId = uuid();
    const issuerDid = `did:web:ssi.truvity.team:${tenantId}`;

    describe('LinkedCredential', () => {
        const resourceId = uuid();
        const blobId = `https://ssi.truvity.team/tenants/${tenantId}/credentials/${resourceId}`;
        const digestMultibase = '12345678';

        const mockResourceCredential: ResourceCredential = {
            kind: {
                name: 'credential',
                version: 1,
            },
            id: resourceId,
            etag: '123',
            revision: 1,
            createdAt: new Date(),
            modifiedAt: new Date(),
            annotations: {},
            labels: {},
            data: {
                id: blobId,
                issuer: issuerDid,
                type: ['VerifiableCredential', 'TestCredential'],
                validFrom: new Date(),
                digestMultibase,
                dataModel: {
                    flavorName: 'VC1_1',
                    id: blobId,
                    issuanceDate: new Date(),
                    issuer: issuerDid,
                    proof: { type: 'DataIntegrityProof' },
                    type: ['VerifiableCredential', 'TestCredential'],
                },
            },
        };

        const mockResourceDraft: ResourceDraft = {
            kind: {
                name: 'credential',
                version: 1,
            },
            id: resourceId,
            etag: '123',
            revision: 1,
            createdAt: new Date(),
            modifiedAt: new Date(),
            annotations: {},
            labels: {},
            data: {
                type: ['VerifiableCredential', 'TestCredential'],
                metaSchema: 'test',
            },
        };

        const mockClient = <ExtendedTruvityClient>{
            drafts: {
                draftLatest: async (id) => {
                    if (id !== resourceId) throw new Error(`Unknown credential ID: ${id}`);
                    return mockResourceDraft;
                },
            },
            credentials: {
                credentialLatest: async (id) => {
                    if (id !== resourceId) throw new Error(`Unknown credential ID: ${id}`);
                    return mockResourceCredential;
                },
                credentialSearch: async (request) => {
                    const data = request?.filter?.[0].data;

                    if (!data) {
                        throw new Error(`Unexpected credential filter query: ${JSON.stringify(request)}`);
                    }

                    if (data.id && (data.id.operator !== 'EQUAL' || data.id.value !== blobId)) {
                        throw new Error(`Unexpected credential "data.id" filter operator: ${JSON.stringify(data.id)}`);
                    }

                    if (
                        data.digestMultibase &&
                        (data.digestMultibase.operator !== 'EQUAL' || data.digestMultibase.value !== digestMultibase)
                    ) {
                        throw new Error(
                            `Unexpected credential "data.digestMultibase" filter operator: ${JSON.stringify(data.digestMultibase)}`,
                        );
                    }

                    return {
                        items: [mockResourceCredential],
                    };
                },
            },
        };

        it.each<[id: string, digestMultibase: string | undefined]>([
            [resourceId, undefined],
            [blobId, undefined],
            [blobId, digestMultibase],
            [`urn:credential:${resourceId}`, undefined],
            [`urn:credential:${resourceId}`, digestMultibase],
        ])(
            'should dereference the LinkedCredential with id(%s) and digestMultibase(%s) correctly',
            async (id, digestMultibase) => {
                const linkedCredential = new LinkedCredential(mockClient, id, digestMultibase);

                expect((await linkedCredential.dereferenceVerifiableCredential()).descriptor).toBe(
                    mockResourceCredential,
                );
            },
        );
        it('should dereference the draft with id(%s) and digestMultibase(%s) correctly', async () => {
            const linkedCredential = new LinkedCredential(mockClient, `urn:draft:${resourceId}`, digestMultibase);

            expect((await linkedCredential.dereferenceDraft()).descriptor).toBe(mockResourceDraft);
        });
    });

    describe('LinkedFile', () => {
        const resourceId = uuid();
        const blobId = `https://ssi.truvity.team/tenants/${tenantId}/files/${resourceId}`;
        const digestMultibase = '12345678';

        const mockResourceFile: ResourceFile = {
            kind: {
                name: 'file',
                version: 1,
            },
            id: resourceId,
            etag: '123',
            revision: 1,
            createdAt: new Date(),
            modifiedAt: new Date(),
            annotations: {},
            labels: {},
            data: {
                id: blobId,
                contentType: 'text/plain; charset=utf-8',
                filesize: 8,
                filename: 'test.txt',
                digestMultibase: digestMultibase,
            },
        };

        const mockClient = <ExtendedTruvityClient>{
            files: {
                fileLatest: async (id) => {
                    if (id !== resourceId) throw new Error(`Unknown file ID: ${id}`);
                    return mockResourceFile;
                },
                fileSearch: async (request) => {
                    const data = request?.filter?.[0].data;

                    if (!data) {
                        throw new Error(`Unexpected file filter query: ${JSON.stringify(request)}`);
                    }

                    if (data.id && (data.id.operator !== 'EQUAL' || data.id.value !== blobId)) {
                        throw new Error(`Unexpected file "data.id" filter operator: ${JSON.stringify(data.id)}`);
                    }

                    if (
                        data.digestMultibase &&
                        (data.digestMultibase.operator !== 'EQUAL' || data.digestMultibase.value !== digestMultibase)
                    ) {
                        throw new Error(
                            `Unexpected file "data.digestMultibase" filter operator: ${JSON.stringify(data.digestMultibase)}`,
                        );
                    }

                    return {
                        items: [mockResourceFile],
                    };
                },
            },
        };

        it.each<[id: string, digestMultibase: string | undefined]>([
            [resourceId, undefined],
            [blobId, undefined],
            [blobId, digestMultibase],
            [`urn:file:${resourceId}`, undefined],
            [`urn:file:${resourceId}`, digestMultibase],
        ])(
            'should dereference the LinkedFile with id(%s) and digestMultibase(%s) correctly',
            async (id, digestMultibase) => {
                const linkedFile = new LinkedFile(mockClient, id, digestMultibase);

                expect((await linkedFile.dereference()).data).toBe(mockResourceFile.data);
            },
        );
    });
});
